import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import {
  collection,
  query,
  getDocs,
  doc,
  getDoc,
  where,
  orderBy,
  limit
} from "firebase/firestore";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Avatar,
  Container,
  Badge,
} from "@mui/material";
import LogoLoader from "./LogoLoader";
import { isLoggedIn } from "../session";
import BottomNav from "./BottomNav";
import Atras from "./atras";

function Chats() {
  const [chats, setChats] = useState([]);
  const [authUserId, setAuthUserId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAuthUserId = async () => {
      const user = await isLoggedIn();
      if (user) {
        setAuthUserId(user.uid);
        console.log("Authenticated User ID:", user.uid);
      } else {
        console.log("No authenticated user found.");
        navigate("/login");
      }
    };
    fetchAuthUserId();
  }, [navigate]);

  useEffect(() => {
    const fetchChats = async () => {
      if (!authUserId) return;

      setLoading(true);
      setError(null);
      try {
        const conversationsRef = collection(db, "conversations");
        const conversationsQuery = query(
          conversationsRef,
          where("participants", "array-contains", authUserId)
        );
        const conversationsSnapshot = await getDocs(conversationsQuery);

        console.log("Number of conversations found:", conversationsSnapshot.size);

        const chatPromises = conversationsSnapshot.docs.map(async (conversationDoc) => {
          const conversationData = conversationDoc.data();
          console.log("Conversation data:", conversationData);

          if (!conversationData.participants || !Array.isArray(conversationData.participants)) {
            console.error("Invalid participants data for conversation:", conversationDoc.id);
            return null;
          }

          const otherUserId = conversationData.participants.find(id => id !== authUserId);

          if (!otherUserId) {
            console.error("Could not find other user ID for conversation:", conversationDoc.id);
            return null;
          }

          // Fetch the last message
          const messagesRef = collection(db, `conversations/${conversationDoc.id}/messages`);
          const lastMessageQuery = query(messagesRef, orderBy("timestamp", "desc"), limit(1));
          const lastMessageSnapshot = await getDocs(lastMessageQuery);
          
          const lastMessage = lastMessageSnapshot.docs[0]?.data() || {};
          console.log("Last message:", lastMessage);

          // Fetch place details
          let placeData = {};
          if (conversationData.placeId) {
            const placeDocRef = doc(db, "places", conversationData.placeId);
            const placeDocSnapshot = await getDoc(placeDocRef);
            placeData = placeDocSnapshot.exists() ? placeDocSnapshot.data() : {};
            console.log("Place data:", placeData);
          } else {
            console.warn("No placeId found for conversation:", conversationDoc.id);
          }

          // Fetch other user's display name
          const otherUserDocRef = doc(db, "users", otherUserId);
          const otherUserDocSnapshot = await getDoc(otherUserDocRef);
          const otherUserDisplayName = otherUserDocSnapshot.exists()
            ? otherUserDocSnapshot.data().displayName
            : "Usuario desconocido";
          console.log("Other user display name:", otherUserDisplayName);

          // Calculate unread messages count
          const unreadMessagesQuery = query(
            messagesRef,
            where("senderId", "==", otherUserId),
            where("read", "==", false)
          );
          const unreadMessagesSnapshot = await getDocs(unreadMessagesQuery);
          const unreadCount = unreadMessagesSnapshot.size;
          console.log("Unread count:", unreadCount);

          return {
            id: conversationDoc.id,
            title: placeData.title || "Lugar sin título",
            imageUrl: placeData.images?.[0] || "",
            lastMessage: lastMessage,
            placeId: conversationData.placeId || "",
            placeUid: otherUserId,
            placeUidDisplayName: otherUserDisplayName,
            unreadCount,
          };
        });

        const chatResults = await Promise.all(chatPromises);
        console.log("Chat results:", chatResults);
        setChats(chatResults.filter(chat => chat !== null));
      } catch (error) {
        console.error("Error fetching chats:", error);
        setError("Error al cargar los chats. Por favor, inténtalo de nuevo más tarde.");
      } finally {
        setLoading(false);
      }
    };

    fetchChats();
  }, [authUserId]);

  const handleChatClick = (conversationId, placeId, placeUid) => {
    navigate(`/chatbox/${placeId}/${placeUid}/${authUserId}`);
  };

  return (
    <Box sx={{ mt: 10 }}>
      <AppBar position="fixed">
        <Toolbar
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-start",
            alignItems: "center",
            width: "100%",
            height: "100%",
          }}
        >
          <Atras />
          <Typography
            variant="h6"
            sx={{
              paddingLeft: "24px",
              paddingTop: "10px",
              fontSize: "24px",
              color: "white",
            }}
            gutterBottom
          >
            Chats
          </Typography>
        </Toolbar>
      </AppBar>
      <Container sx={{ mt: 3, minHeight: '10vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        {loading ? (
          <LogoLoader label="Cargando chats..." minHeight={240} />
        ) : error ? (
          <Typography variant="body1" color="error" sx={{ mt: 5, textAlign: "center" }}>
            {error}
          </Typography>
        ) : chats.length > 0 ? (
          <List>
            {chats.map((chat) => (
              <div key={chat.id}>
                <ListItem
                  button
                  onClick={() => handleChatClick(chat.id, chat.placeId, chat.placeUid)}
                >
                  <Badge
                    badgeContent={chat.unreadCount}
                    color="error"
                    invisible={chat.unreadCount === 0}
                  >
                    <Avatar
                      src={chat.imageUrl}
                      alt={chat.title}
                      sx={{
                        border: "2.5px solid #34d399",
                        padding: "5px",
                        marginRight: "15px",
                      }}
                    />
                  </Badge>
                  <ListItemText
                    primary={chat.title}
                    secondary={`${chat.placeUidDisplayName}: ${chat.lastMessage.content || ""}`}
                  />
                </ListItem>
                <Divider />
              </div>
            ))}
          </List>
        ) : (
          <Typography variant="body1" sx={{ mt: 5, textAlign: "center" }}>
            No hay chats disponibles.
          </Typography>
        )}
      </Container>
      <BottomNav />
    </Box>
  );
}

export default Chats;